import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

function parseStat(value: string): {
  prefix: string;
  number: number;
  suffix: string;
  useCommas: boolean;
} {
  const match = value.match(/^([^0-9]*)([\d,]+)(.*)$/);
  if (!match) return { prefix: '', number: 0, suffix: value, useCommas: false };
  return {
    prefix: match[1] || '',
    number: Number(match[2].replace(/,/g, '')) || 0,
    suffix: match[3] || '',
    useCommas: match[2].includes(','),
  };
}

interface CountUpProps {
  value: string;
  className?: string;
}

/** Animates a stat string like "10,000+" from 0 when scrolled into view. */
export function CountUp({ value, className }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px' });
  const { prefix, number, suffix, useCommas } = parseStat(value);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;

    let frame = 0;
    const duration = 1600;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(number * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, number]);

  const formatted = useCommas ? display.toLocaleString('en-US') : String(display);

  return (
    <span ref={ref} className={className} aria-hidden>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

export default CountUp;
